/**
 * Buyer-facing declared-scope coverage panel. Pure presentational; driven
 * entirely by the drift-gated coverage claim (GET /v1/coverage-claim) so the
 * statements cannot diverge from the registry proof.
 *
 * Wording is deliberately "evidence readiness / declared scope / advisory
 * retrieved context" and MUST NOT imply a legal determination, an automated
 * regulatory ruling, or automatic approval (ADR-0002).
 */

import type { CoverageClaimResponse } from "../services/api";

interface Props {
  claim: CoverageClaimResponse["claim"] | null;
}

const cardStyle: React.CSSProperties = {
  background: "var(--color-surface-elevated)",
  border: "1px solid var(--color-border)",
  borderRadius: "var(--radius-8)",
  padding: "var(--space-12)",
  margin: "var(--space-8) 0",
};

export function CoverageScopePanel({ claim }: Props) {
  return (
    <section
      data-testid="coverage-scope-panel"
      aria-label="Declared-scope coverage"
      style={cardStyle}
    >
      <header style={{ marginBottom: "var(--space-8)" }}>
        <h3 className="type-h3" style={{ margin: 0, fontSize: 13 }}>
          Declared-scope coverage · evidence readiness
        </h3>
        <p
          className="type-caption"
          style={{
            margin: "var(--space-4) 0 0 0",
            color: "var(--color-text-secondary)",
          }}
        >
          What RegPulse declares as v0.1 evidence-ready, within a declared
          scope. Advisory only — retrieved context and coverage status are not
          a legal determination and require human review.
        </p>
      </header>

      {claim === null ? (
        <p
          className="type-caption"
          style={{ margin: 0, color: "var(--color-text-secondary)" }}
        >
          Declared scope loading… (advisory; the demo proceeds without it).
        </p>
      ) : (
        <div style={{ display: "grid", gap: "var(--space-8)", fontSize: 12 }}>
          <div>
            <strong>EU / UNECE baseline core — complete (declared scope):</strong>
            <ul style={{ margin: "var(--space-4) 0 0 0", paddingLeft: "var(--space-16)" }}>
              {claim.declaredScope.coreRules.map((r) => (
                <li key={r.ruleId}>
                  {r.instrument} <code>({r.ruleId})</code>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <strong>Member states — complete (declared scope):</strong>{" "}
            {claim.declaredScope.completeJurisdictions.join(" · ")}
          </div>

          <div>
            <strong>Explicit v0.1 scope boundary</strong> — tracked, deliberately
            not claimed complete, no hidden unknowns:
            <ul style={{ margin: "var(--space-4) 0 0 0", paddingLeft: "var(--space-16)" }}>
              {claim.scopeBoundary.map((b) => (
                <li key={b.ruleId}>
                  {b.instrument} <code>({b.ruleId})</code> — {b.reason}
                </li>
              ))}
            </ul>
          </div>

          <p
            data-testid="coverage-status-vocabulary"
            className="type-caption"
            style={{ margin: 0, color: "var(--color-text-secondary)" }}
          >
            Status vocabulary (COVERAGE-MATRIX): <strong>complete</strong> = proven
            within the declared scope · <strong>out_of_scope_v0_1</strong> = the
            tracked boundary above. Secondary markets (e.g. NL · IT · PL · SE) are{" "}
            <strong>partial</strong> / <strong>planned</strong> and surface as
            placeholder / curation-gap cards on the board — never as a completeness
            claim.
          </p>

          <p
            className="type-caption"
            style={{ margin: 0, color: "var(--color-text-secondary)" }}
          >
            {claim.advisoryNote}
          </p>
        </div>
      )}
    </section>
  );
}
